/**
 * FeedsListPane
 * ---
 * Extracted from `src/pages/MyFeedReader.tsx` to keep the page module under our
 * size budget. Renders the left-rail list of feed subscriptions with per-feed
 * row actions (sync, mark-all-read, remove). Pure presentation — all state
 * lives on the page.
 */
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { CheckCheck, Loader2, MoreHorizontal, RefreshCw, Trash2 } from "lucide-react";
import type { FeedSubscription } from "@/lib/feedReader";
import { formatRelative } from "./feedReaderHelpers";

type Props = {
  subscriptions: FeedSubscription[];
  unreadCounts: Record<string, number>;
  selectedSub: string | null;
  setSelectedSub: (id: string | null) => void;
  loading: boolean;
  syncingId: string | null;
  onSync: (id: string) => void;
  onMarkAllRead: (id: string) => void;
  onDelete: (id: string) => void;
  showChips: boolean;
  wsLabel: (id: string) => string;
  active: { id: string } | null;
  activeFeedCount: number;
  activeFeedCap: number;
  extractionUsed: number;
  extractionCap: number;
};

export const FeedsListPane = ({
  subscriptions,
  unreadCounts,
  selectedSub,
  setSelectedSub,
  loading,
  syncingId,
  onSync,
  onMarkAllRead,
  onDelete,
  showChips,
  wsLabel,
  active,
  activeFeedCount,
  activeFeedCap,
  extractionUsed,
  extractionCap,
}: Props) => {
  return (
    <div className="flex h-full flex-col">
      <div className="px-3 py-3 border-b border-border/60 space-y-1">
        <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Feeds ({subscriptions.length})
        </h2>
        {active && (
          <>
            <p className="text-[11px] text-muted-foreground">
              Using <span className="font-medium text-foreground">{activeFeedCount}</span> of{" "}
              <span className="font-medium text-foreground">{activeFeedCap}</span> feeds in this workspace
            </p>
            <p className="text-[11px] text-muted-foreground">
              <span className="font-medium text-foreground">{extractionUsed}</span> of{" "}
              <span className="font-medium text-foreground">{extractionCap}</span> article fetches today
            </p>
          </>
        )}
      </div>
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          <button
            type="button"
            onClick={() => setSelectedSub(null)}
            className={`w-full text-left rounded px-2 py-2 text-sm hover:bg-muted ${selectedSub === null ? "bg-muted font-medium" : ""}`}
          >
            All feeds
          </button>
          {subscriptions.length === 0 && !loading && (
            <p className="px-2 py-3 text-xs text-muted-foreground">No feeds yet. Add one to get started.</p>
          )}
          {subscriptions.map((s) => {
            const unread = unreadCounts[s.id] ?? 0;
            const isActive = selectedSub === s.id;
            return (
              <div
                key={s.id}
                className={`group rounded px-2 py-2 text-sm flex items-start gap-2 hover:bg-muted ${isActive ? "bg-muted" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => setSelectedSub(s.id)}
                  className="flex-1 min-w-0 text-left flex items-start gap-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1">
                      <span className={`truncate ${isActive ? "font-medium" : ""}`}>{s.title}</span>
                      {unread > 0 && (
                        <Badge variant="secondary" className="ml-auto shrink-0">{unread}</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {showChips ? `${wsLabel(s.workspace_id)} · ` : ""}
                      Synced {formatRelative(s.last_synced_at)}
                    </div>
                    {s.last_error_message && (
                      <div className="text-xs text-destructive truncate" title={s.last_error_message}>
                        ⚠ {s.last_error_message}
                      </div>
                    )}
                  </div>
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => e.stopPropagation()}
                      title="Feed actions"
                    >
                      <MoreHorizontal className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => onSync(s.id)}
                      disabled={syncingId === s.id}
                    >
                      {syncingId === s.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      Sync now
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onMarkAllRead(s.id)}
                      disabled={!unread}
                    >
                      <CheckCheck className="h-4 w-4 mr-2" /> Mark all read
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={(e) => e.preventDefault()}
                        >
                          <Trash2 className="h-4 w-4 mr-2" /> Remove feed
                        </DropdownMenuItem>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove this feed?</AlertDialogTitle>
                          <AlertDialogDescription>
                            All items from &ldquo;{s.title}&rdquo; will be removed from your inbox. Items already saved to My Library are kept.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDelete(s.id)}>Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};
